import { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  Download,
  HelpCircle,
  Info,
  RefreshCw,
  X,
} from "lucide-react";
import { buildSupportTicketPrompt } from "../../lib/support-ticket-prompt";
import { activateSkillConnectors } from "../../lib/skill-connectors";
import type { ElectronAPI } from "../../types/electron";
import { ManualSupportTicketDialog } from "./ManualSupportTicketDialog";
import { runVersionBackendCheck } from "./version-info-backend-check";

interface VersionInfoPopoverProps {
  version: string;
}

type UpdatePhase =
  | "idle"
  | "checking"
  | "downloading"
  | "ready"
  | "current"
  | "installing"
  | "error";

interface UpdateState {
  phase: UpdatePhase;
  message: string | null;
  latest?: string | null;
}

type BackendCheckPhase = "idle" | "checking" | "ok" | "error";

export function VersionInfoPopover({ version }: VersionInfoPopoverProps) {
  const [open, setOpen] = useState(false);
  const [updateState, setUpdateState] = useState<UpdateState>({
    phase: "idle",
    message: null,
  });
  const [backendCheckPhase, setBackendCheckPhase] =
    useState<BackendCheckPhase>("idle");
  const [backendCheckMessage, setBackendCheckMessage] = useState<string | null>(
    null,
  );
  const [manualTicketOpen, setManualTicketOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const electronApi =
    typeof window === "undefined"
      ? undefined
      : (window as unknown as { electron?: Partial<ElectronAPI> }).electron;
  const updaterAvailable = Boolean(
    electronApi?.checkForUpdate &&
    electronApi?.downloadUpdate &&
    electronApi?.installUpdate,
  );
  const backendCheckAvailable = Boolean(electronApi?.backendEngine?.restart);
  const updateBusy =
    updateState.phase === "checking" ||
    updateState.phase === "downloading" ||
    updateState.phase === "installing";

  function updateNeedsDownload(reason?: string) {
    return reason?.toLowerCase().includes("not downloaded") ?? false;
  }

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
      setManualTicketOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function handleReportIssue() {
    const prompt = buildSupportTicketPrompt({
      version,
      surface: document.title || "Fintheon",
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
    const connectorIds = activateSkillConnectors("support_ticket");
    window.dispatchEvent(new Event("fintheon:open-chat-panel"));
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("fintheon:open-sidebar-chat-skill", {
          detail: { skillId: "support_ticket", prompt, connectorIds },
        }),
      );
    }, 80);
    setOpen(false);
    setManualTicketOpen(false);
  }

  function handleManualTicket() {
    setOpen(false);
    setManualTicketOpen(true);
  }

  async function handleCheckUpdates() {
    if (!electronApi?.checkForUpdate || !electronApi?.downloadUpdate) {
      setUpdateState({
        phase: "error",
        message: "Desktop updater unavailable.",
      });
      return;
    }

    setUpdateState({ phase: "checking", message: "Checking for updates." });
    try {
      const status = await electronApi.checkForUpdate();
      if (!status.ok) {
        throw new Error(status.reason || "Update check failed.");
      }
      if (!status.updateAvailable || !status.latest) {
        setUpdateState({
          phase: "current",
          message: "Fintheon is up to date.",
          latest: status.latest,
        });
        return;
      }
      if (status.downloaded) {
        setUpdateState({
          phase: "ready",
          message: `Update ${status.latest} is ready.`,
          latest: status.latest,
        });
        return;
      }

      setUpdateState({
        phase: "downloading",
        message: `Downloading ${status.latest}.`,
        latest: status.latest,
      });
      const downloaded = await electronApi.downloadUpdate(status.latest);
      if (!downloaded.ok) {
        throw new Error(downloaded.reason || "Update download failed.");
      }
      setUpdateState({
        phase: "ready",
        message: `Update ${downloaded.version || status.latest} is ready.`,
        latest: downloaded.version || status.latest,
      });
    } catch (error) {
      setUpdateState({
        phase: "error",
        message:
          error instanceof Error ? error.message : "Update check failed.",
      });
    }
  }

  async function handleInstallUpdate() {
    if (!electronApi?.installUpdate) {
      setUpdateState({
        phase: "error",
        message: "Desktop updater unavailable.",
      });
      return;
    }

    setUpdateState({
      phase: "installing",
      message: "Opening installer.",
      latest: updateState.latest,
    });
    window.dispatchEvent(new Event("fintheon:update-installing"));
    try {
      let result = await electronApi.installUpdate(
        updateState.latest ?? undefined,
      );
      if (
        !result.ok &&
        updateNeedsDownload(result.reason) &&
        updateState.latest &&
        electronApi.downloadUpdate
      ) {
        setUpdateState({
          phase: "downloading",
          message: `Downloading ${updateState.latest}.`,
          latest: updateState.latest,
        });
        const downloaded = await electronApi.downloadUpdate(updateState.latest);
        if (!downloaded.ok) {
          throw new Error(downloaded.reason || "Update download failed.");
        }
        setUpdateState({
          phase: "installing",
          message: "Opening installer.",
          latest: updateState.latest,
        });
        result = await electronApi.installUpdate(updateState.latest);
      }
      if (!result.ok) {
        throw new Error(result.reason || "Install handoff failed.");
      }
      setUpdateState({
        phase: "ready",
        message: result.installing
          ? "Installer starting."
          : "Installer opened.",
        latest: updateState.latest,
      });
    } catch (error) {
      setUpdateState({
        phase: "error",
        message:
          error instanceof Error ? error.message : "Install handoff failed.",
        latest: updateState.latest,
      });
    }
  }

  async function handleCheckBackend() {
    if (!electronApi?.backendEngine?.restart) {
      setBackendCheckPhase("error");
      setBackendCheckMessage("Backend controls unavailable.");
      return;
    }
    setBackendCheckPhase("checking");
    setBackendCheckMessage("Restarting backend and checking runtime.");
    const result = await runVersionBackendCheck({ electronApi });
    setBackendCheckPhase(result.phase);
    setBackendCheckMessage(result.message);
  }

  function updateButtonLabel() {
    if (updateState.phase === "checking") return "Checking";
    if (updateState.phase === "downloading") return "Downloading";
    if (updateState.phase === "installing") return "Installing";
    if (updateState.phase === "ready") return "Install Update";
    return "Check Updates";
  }

  function updateButtonIcon() {
    if (updateState.phase === "ready") return <Download size={13} />;
    if (updateState.phase === "current") return <CheckCircle2 size={13} />;
    return (
      <RefreshCw
        size={13}
        className={updateBusy ? "animate-spin" : undefined}
      />
    );
  }

  return (
    <div ref={rootRef} className="relative flex items-center">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="tracking-[0.12em] text-[10px] text-[var(--fintheon-text)]/75 tabular-nums transition-colors hover:text-[var(--fintheon-accent)]"
        title="Version info"
      >
        {version}
      </button>
      {open && (
        <div className="absolute bottom-7 left-0 z-[80] w-[286px] rounded-md border border-[var(--fintheon-accent)]/20 bg-[#090704]/96 p-3 shadow-2xl backdrop-blur-xl">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2">
              <Info
                size={15}
                className="mt-0.5 shrink-0 text-[var(--fintheon-accent)]"
              />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--fintheon-accent)]">
                  Version Info
                </p>
                <p className="mt-1 text-[12px] text-[var(--fintheon-text)]/82">
                  Fintheon Epoch {version}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[var(--fintheon-text)]/34 transition-colors hover:text-[var(--fintheon-text)]/72"
              title="Close"
            >
              <X size={13} />
            </button>
          </div>
          <div className="mt-3 grid gap-1.5 text-[10px] text-[var(--fintheon-text)]/48">
            <div className="flex justify-between gap-3">
              <span>Runtime</span>
              <span className="truncate text-[var(--fintheon-text)]/70">
                {electronApi ? "Desktop" : "Web"}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span>Opened</span>
              <span className="text-[var(--fintheon-text)]/70">
                {new Date().toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={
              updateState.phase === "ready"
                ? handleInstallUpdate
                : handleCheckUpdates
            }
            disabled={!updaterAvailable || updateBusy}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-[var(--fintheon-accent)]/28 bg-[var(--fintheon-accent)]/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--fintheon-accent)] transition-colors hover:bg-[var(--fintheon-accent)]/16 disabled:cursor-not-allowed disabled:opacity-45"
          >
            {updateButtonIcon()}
            {updateButtonLabel()}
          </button>
          <button
            type="button"
            onClick={handleCheckBackend}
            disabled={
              !backendCheckAvailable || backendCheckPhase === "checking"
            }
            className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-md border border-[var(--fintheon-accent)]/18 bg-transparent px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--fintheon-text)]/68 transition-colors hover:border-[var(--fintheon-accent)]/30 hover:text-[var(--fintheon-accent)] disabled:cursor-not-allowed disabled:opacity-45"
          >
            <RefreshCw
              size={13}
              className={
                backendCheckPhase === "checking" ? "animate-spin" : undefined
              }
            />
            Check Backend
          </button>
          {updateState.message ? (
            <p
              className="mt-2 line-clamp-2 text-[10px] leading-snug text-[var(--fintheon-text)]/58"
              aria-live="polite"
            >
              {updateState.message}
            </p>
          ) : null}
          {backendCheckMessage ? (
            <p
              className={`mt-1.5 line-clamp-2 text-[10px] leading-snug ${
                backendCheckPhase === "error"
                  ? "text-red-300/72"
                  : "text-[var(--fintheon-text)]/58"
              }`}
              aria-live="polite"
            >
              {backendCheckMessage}
            </p>
          ) : null}
          <button
            type="button"
            onClick={handleReportIssue}
            className="fintheon-glass-cta relative mt-3 flex w-full items-center justify-center gap-2 overflow-hidden rounded-md border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--fintheon-accent)] transition-colors"
          >
            <HelpCircle size={13} />
            Create Support Ticket
          </button>
          <button
            type="button"
            onClick={handleManualTicket}
            className="fintheon-glass-cta relative mt-1.5 flex w-full items-center justify-center gap-2 overflow-hidden rounded-md border px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--fintheon-accent)] transition-colors"
          >
            <HelpCircle size={13} />
            Manual Ticket
          </button>
        </div>
      )}
      <ManualSupportTicketDialog
        open={manualTicketOpen}
        version={version}
        onClose={() => setManualTicketOpen(false)}
      />
    </div>
  );
}
